import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatCents } from '../lib/money';

function PendingSection({ title, events, onApprove, onReject }) {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      {events.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Nothing pending.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Plan</th><th>Amount</th><th>Reason</th><th>Requested by</th><th>When</th><th>Commission flag</th><th />
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td><Link to={`/orders/${e.order_id}`}>{e.order_id.slice(0, 8)}</Link></td>
                <td>{formatCents(e.amount_cents)}</td>
                <td>{e.reason_code}{e.reason_text ? ` — ${e.reason_text}` : ''}</td>
                <td>{e.actor_user_id}</td>
                <td>{new Date(e.created_at).toLocaleString()}</td>
                <td>
                  {e.commission_flag ? (
                    <span className="commission-flag commission-flag-inline">{e.commission_flag_reason}</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-primary" onClick={() => onApprove(e.id)}>Approve</button>{' '}
                  <button className="btn btn-danger" onClick={() => onReject(e.id)}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function ApprovalsQueue() {
  const [refunds, setRefunds] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [error, setError] = useState(null);
  // WS7: set right after an approved cancellation comes back with a linked
  // HCP job id -- HouseCallPro's API has no confirmed way to cancel/delete
  // a job (see cancellationService.js's hcpJobIdFor), so this is the whole
  // feature: a hard-to-miss reminder with a direct link, instead of a
  // silent no-op that leaves a tech dispatched to a canceled customer.
  const [hcpReminderJobId, setHcpReminderJobId] = useState(null);

  async function load() {
    try {
      const [r, c] = await Promise.all([api.get('/refunds/pending'), api.get('/cancellations/pending')]);
      setRefunds(r.events);
      setCancellations(c.events);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function approveRefund(id) {
    try {
      await api.post(`/refunds/${id}/approve`);
      load();
    } catch (err) {
      setError(err.message);
    }
  }
  async function rejectRefund(id) {
    const reason = window.prompt('Rejection reason (optional):') || '';
    try {
      await api.post(`/refunds/${id}/reject`, { reason });
      load();
    } catch (err) {
      setError(err.message);
    }
  }
  async function approveCancellation(id) {
    try {
      const { hcpJobId } = await api.post(`/cancellations/${id}/approve`);
      load();
      if (hcpJobId) {
        setHcpReminderJobId(hcpJobId);
      }
    } catch (err) {
      setError(err.message);
    }
  }
  async function rejectCancellation(id) {
    const reason = window.prompt('Rejection reason (optional):') || '';
    try {
      await api.post(`/cancellations/${id}/reject`, { reason });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div>
      <h2>Approvals</h2>
      {error && <div className="error-banner">{error}</div>}
      <PendingSection title="Refunds pending approval" events={refunds} onApprove={approveRefund} onReject={rejectRefund} />
      <PendingSection title="Cancellations pending approval" events={cancellations} onApprove={approveCancellation} onReject={rejectCancellation} />

      {hcpReminderJobId && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Close out the HCP job too</h3>
            <p>
              This plan had a linked HouseCallPro job. HCP doesn't support canceling a job through our
              integration, so it won't get closed out automatically -- if you don't handle it there, a tech can
              still get dispatched to this now-canceled customer.
            </p>
            <p>
              <a href={`https://pro.housecallpro.com/app/jobs/${hcpReminderJobId}`} target="_blank" rel="noreferrer">
                Open this job in HouseCallPro
              </a>
            </p>
            <button type="button" className="btn btn-primary" onClick={() => setHcpReminderJobId(null)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
